import React, { useState } from 'react';
import PropTypes from 'prop-types';

import platform from 'platform';
import appConfig from 'config';
import { actions as authActions, selectors as authSelectors } from 'state/auth';
import { selectors as errorsSelectors } from 'state/errors';
import { selectors as settingsSelectors } from 'state/settings';
import { useDispatch, useSelector } from 'react-redux';
import useNavigateToMainPage from 'utils/hooks/useNavigateToMainPage';
import { useTranslation } from 'react-i18next';
// components
import { Button, Field, Radios } from '@ontruck/orc';
import UserModal from 'components/UserModal';
import { Title, Text } from 'refactored/ui/typography';
import { Main, Separator, GridRow } from 'refactored/ui/layout';
import { ButtonMaxWithdOnMobile } from 'refactored/ui/form';
import AuthenticatedUserView from 'refactored/ui/views/authenticatedUserView';
// styles
import styled, { css } from 'styled-components';
import { gridSizes, media } from 'utils/style';
import { userIsAuthenticatedRedir } from 'components/auth/hoc';

const NameFieldsContainerRegularCSS = css`
  display: flex;
  margin-bottom: ${gridSizes('M')};

  & > div {
    flex: 1;
    margin-right: ${gridSizes('M')};
  }

  & > div:last-child {
    margin-right: 0;
  }
`;
const NameFieldsContainerMobileCSS = css`
  flex-direction: column;
  & > div {
    margin-right: 0;
    width: 100%;
  }
  & > div:last-child {
    margin-top: ${gridSizes('M')};
  }
`;
const NameFieldsContainer = styled.div`
  ${NameFieldsContainerRegularCSS}
  ${media.lessThan('extraSmall')`${NameFieldsContainerMobileCSS}`}
`;

const inviteButtonContainerRegularCSS = css`
  margin-top: ${gridSizes('M')};
  button {
    background-color: #e0eaff;
    color: #002575;
    .white {
      color: #002575;
      border: 1px solid #002575;
    }
    &:hover {
      background-color: #6797fe;
    }
  }
`;
const inviteButtonContainerMobileCSS = css`
  button {
    width: 100%;
  }
`;
const InviteButtonContainer = styled(Button)`
  ${inviteButtonContainerRegularCSS}
  ${media.lessThan('extraSmall')`${inviteButtonContainerMobileCSS}`}
`;

const radiosContainerRegularCSS = css`
  margin: ${gridSizes('M')} 0;
`;
const RadiosContainer = styled.div`
  ${radiosContainerRegularCSS}
`;

const NavigatorInfo = ({ user, email }) => (
  <GridRow>
    <Title level="h2">Info</Title>
    <p>
      {user && user.shipper_id} - {user && user.id} - {user && email}
    </p>

    <p>{platform.description}</p>
    <p>{appConfig.APP_VERSION}</p>
  </GridRow>
);

NavigatorInfo.propTypes = {
  user: PropTypes.string,
};

NavigatorInfo.defaultProps = {
  user: '',
};

const SettingsPage = ({ className }) => {
  const { t } = useTranslation('Settings');
  const dispatch = useDispatch();
  const handleBack = useNavigateToMainPage();

  const { email, ...user } = useSelector((state) =>
    authSelectors.getUser(state)
  );
  const getSpecificUserData = () => {
    const { first_name, last_name, phone, commercial_communications_accepted } =
      user;
    return { first_name, last_name, phone, commercial_communications_accepted };
  };
  const shipper = useSelector((state) => authSelectors.getShipper(state));
  const { tax_number, company_name, id } = shipper;
  const errors = useSelector((state) => errorsSelectors.getErrors(state));

  const [inviteUserModalOpened, setInviteUserModalOpened] = useState(
    useSelector((state) => settingsSelectors.getInviteUserModalOpened(state))
  );
  const [userData, setUserData] = useState(getSpecificUserData());
  const { first_name, last_name, phone, commercial_communications_accepted } =
    userData;

  const handleFieldChange = (_, { name, value }) => {
    if (name === 'commercial_communications_accepted') {
      value = value === 'YES';
    }
    setUserData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSaveChanges = () => {
    dispatch(authActions.updateUser({ userData }));
  };
  const openInviteUserModal = () => {
    setInviteUserModalOpened(true);
  };
  const closeInviteUserModal = () => {
    setInviteUserModalOpened(false);
  };

  return (
    <AuthenticatedUserView className={className} handleBack={handleBack}>
      <Main>
        <GridRow>
          <Title>{t('title')}</Title>
        </GridRow>

        <GridRow>
          <Title level="h2">{t('your_info')}</Title>

          <Text variant="label">{t('email')}</Text>
          <Text>{email}</Text>

          <NameFieldsContainer>
            <Field
              label={t('name')}
              name="first_name"
              id="first_name"
              size="L"
              type="text"
              value={first_name}
              required
              error={!!errors.settings_first_name}
              onChange={handleFieldChange}
            />

            <Field
              label={t('last_name')}
              name="last_name"
              id="last_name"
              size="L"
              type="text"
              value={last_name}
              required
              error={!!errors.settings_last_name}
              onChange={handleFieldChange}
            />
          </NameFieldsContainer>

          <Field
            label={t('phone')}
            name="phone"
            id="phone"
            size="L"
            type="tel"
            fullWidth
            value={phone}
            onChange={handleFieldChange}
          />
        </GridRow>

        <Separator />

        <GridRow>
          <Title level="h2">{t('company')}</Title>

          <Text variant="label">{t('company_name')}</Text>
          <Text>{company_name}</Text>

          <Text variant="label">{t('shipper_tax_number')}</Text>
          <Text>{tax_number}</Text>

          <Text variant="help">{t('invitation_help_text')}</Text>
          <InviteButtonContainer
            onClick={openInviteUserModal}
            text={t('invite_partner')}
            skin="secondary"
          />
        </GridRow>

        <Separator />

        <GridRow>
          <Title level="h2">{t('email_news')}</Title>
          <Text variant="help">{t('email_news_help_text')}</Text>
          <RadiosContainer>
            <Radios
              size="L"
              name="commercial_communications_accepted"
              value={commercial_communications_accepted ? 'YES' : 'NO'}
              items={[
                { value: 'YES', label: t('accept_email_news') },
                { value: 'NO', label: t('reject_email_news') },
              ]}
              onChange={handleFieldChange}
            />
          </RadiosContainer>
        </GridRow>
        <Separator />

        {<NavigatorInfo t={t} user={user} email={email} />}

        <GridRow className="actions">
          <ButtonMaxWithdOnMobile
            onClick={handleSaveChanges}
            text={t('save_changes')}
            size="M"
          />
        </GridRow>
      </Main>

      {inviteUserModalOpened && (
        <UserModal onClose={closeInviteUserModal} shipperId={id} />
      )}
    </AuthenticatedUserView>
  );
};

SettingsPage.propTypes = {
  className: PropTypes.string,
};

SettingsPage.defaultProps = {
  className: '',
};

export default userIsAuthenticatedRedir(SettingsPage);
